import { AssetClassLevel, FinancialProductStrategy } from '@generated/graphql';
import type { AssetClassBranch, Branch } from '@interfaces/financialProduct';

import type { TranslatedString } from '@interfaces/shared';
import type { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { calculateDurationSeconds } from '@utils/analytics/eventMetrics';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ModelPortfolioData } from '../createModelPortfolio/createModelPortfolio.types';
import {
  DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  MODEL_PORTFOLIO_GROUP_BY,
  type ModelPortfolioGroupBy,
} from '../createModelPortfolio/modelPortfolioGroupBy';
import { useSaveModelPortfolio } from '../utils/useSaveModelPortfolio';
import { AddModelPortfoliosIPSAndAssetClassesVisual } from './addModelPortfoliosIPSAndAssetClasses.visual';
import type { AssetClassTargetAllocation } from './assetClassTargetAllocation.types';

/**
 * Groups securities by asset class and produces the initial target-allocation
 * rows for the IPS step. Securities that have no asset class (undefined id)
 * are silently skipped. Asset classes already present in
 * `existingAssetClassIds` are not duplicated.
 */
export const buildAutoTargetAllocations = (
  securities: Branch[],
  groupBy: ModelPortfolioGroupBy,
  existingAssetClassIds: Set<string>,
): AssetClassTargetAllocation[] => {
  if (!securities?.length) return [];

  const grouped = securities.reduce(
    (acc: { [key: string]: Branch[] }, security: Branch) => {
      const assetClassId =
        groupBy === MODEL_PORTFOLIO_GROUP_BY.PRIMARY
          ? security.financialProduct?.primaryAssetClass?.id
          : security.financialProduct?.secondaryAssetClass?.id;

      if (assetClassId && !existingAssetClassIds.has(assetClassId)) {
        if (!acc[assetClassId]) {
          acc[assetClassId] = [];
        }
        acc[assetClassId].push(security);
      }
      return acc;
    },
    {},
  );

  return Object.entries(grouped).map(([assetClassId, securitiesToGroup]) => ({
    secondaryAssetClass: { id: assetClassId },
    actualPercentage: securitiesToGroup.reduce(
      (sum, security) => sum + security.percentage,
      0,
    ),
    min: 0,
    max: 100,
    color: 'supportOne' as const,
    fromSecurity: true,
    securityPrimaryAssetClassId:
      securitiesToGroup[0].financialProduct?.primaryAssetClass?.id ?? undefined,
    securitySecondaryAssetClassId:
      securitiesToGroup[0].financialProduct?.secondaryAssetClass?.id ??
      undefined,
  }));
};

/**
 * For ASSET_CLASS strategy models, ensures every `assetClassBranch` has a
 * corresponding row in `targetAllocations`. Branches that are already
 * represented are left untouched; missing ones are added with min=0/max=100.
 * Returns the model unchanged for all other strategies.
 */
export const withMissingBranchAllocations = (
  model: ModelPortfolioData,
): ModelPortfolioData => {
  if (
    model.strategy !== FinancialProductStrategy.AssetClass ||
    !model.assetClassBranches?.length
  ) {
    return model;
  }

  const existingIds = new Set<string>(
    ((model.targetAllocations ?? []) as AssetClassTargetAllocation[])
      .map((t) => t.secondaryAssetClass?.id)
      .filter((id): id is string => Boolean(id)),
  );

  const missing: AssetClassTargetAllocation[] = (
    model.assetClassBranches as AssetClassBranch[]
  )
    .filter((b) => b.assetClass?.id && !existingIds.has(b.assetClass.id))
    .map((branch) => ({
      secondaryAssetClass: { id: branch.assetClass.id },
      min: 0,
      max: 100,
      actualPercentage: 0,
      color: 'supportOne' as const,
      fromSecurity: false,
    }));

  if (!missing.length) return model;

  return {
    ...model,
    targetAllocations: [
      ...((model.targetAllocations ?? []) as AssetClassTargetAllocation[]),
      ...missing,
    ],
  };
};

/**
 * Initialises IPS target allocations for a model portfolio step entry.
 *
 * ASSET_CLASS: allocations come exclusively from `assetClassBranches` via
 * `withMissingBranchAllocations`. Skips `buildAutoTargetAllocations` to avoid
 * keying IPS rows off the financial-product's `primaryAssetClass.id`, which can
 * differ from the branch's `assetClass.id` and cause duplicates / invalid
 * ObjectId errors on save (OVT-6062). `groupBy` is derived from `assetClassLevel`.
 *
 * STANDARD: auto-generates initial IPS rows from `model.securities` via
 * `buildAutoTargetAllocations`, deduplicating against existing allocations.
 */
export const initializeIPSAllocations = (
  model: ModelPortfolioData,
): ModelPortfolioData => {
  if (model.strategy === FinancialProductStrategy.AssetClass) {
    // TERTIARY has no groupBy option today — falls back to PRIMARY.
    const derivedGroupBy =
      model.assetClassLevel === AssetClassLevel.Secondary
        ? MODEL_PORTFOLIO_GROUP_BY.SECONDARY
        : MODEL_PORTFOLIO_GROUP_BY.PRIMARY;
    return withMissingBranchAllocations({
      ...model,
      groupBy: model.groupBy ?? derivedGroupBy,
    });
  }

  const existingIds = new Set<string>(
    (model.targetAllocations ?? [])
      .map((t) => t.secondaryAssetClass?.id)
      .filter((id): id is string => Boolean(id)),
  );

  const autoAllocations = buildAutoTargetAllocations(
    model.securities ?? [],
    model.groupBy ?? DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
    existingIds,
  );

  return withMissingBranchAllocations({
    ...model,
    targetAllocations: [...(model.targetAllocations ?? []), ...autoAllocations],
  });
};

export type AddModelPortfoliosIPSAndAssetClassesOptions = {
  title: TranslatedString;
  ips: {
    enabled: boolean;
    label: TranslatedString;
    options: {
      description: {
        enabled: boolean;
        label: TranslatedString;
        required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      };
      objective: {
        enabled: boolean;
        label: TranslatedString;
        required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      };
      liquidity: {
        enabled: boolean;
        label: TranslatedString;
        required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
      };
    };
  };
  disclaimer: {
    enabled: boolean;
    label: TranslatedString;
    required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
  };
  assetClassesDistribution: {
    enabled: boolean;
    label: TranslatedString;
    required: FieldRequiredType.ERROR | FieldRequiredType.NOT_REQUIRED;
  };
  buttonSaveForLater: {
    enabled: boolean;
    label: TranslatedString;
  };
};

type AddModelPortfoliosIPSAndAssetClassesProps = {
  options: AddModelPortfoliosIPSAndAssetClassesOptions;
  workflowCompletion: any;
  onNext: () => void | Promise<void>;
  stepLoading: boolean;
};

export const AddModelPortfoliosIPSAndAssetClasses = ({
  options,
  workflowCompletion,
  onNext,
  stepLoading,
}: AddModelPortfoliosIPSAndAssetClassesProps) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { workflowData, setWorkflowData, updateWorkflowContext } =
    useContext(WorkflowContext);
  const stepStartTime = useRef<Date | null>(null);
  // Track step start time when component mounts
  useEffect(() => {
    stepStartTime.current = new Date();
  }, []);

  const [modelPortfolioData, setModelPortfolioData] =
    useState<ModelPortfolioData>(() =>
      initializeIPSAllocations(workflowData.modelPortfolioDetails),
    );

  const { saveModelPortfolio, loading } = useSaveModelPortfolio();
  const navigate = useNavigate();

  const handleUpdateModelPortfolioData = async (data: ModelPortfolioData) => {
    setModelPortfolioData({ ...modelPortfolioData, ...data });
    setWorkflowData({
      ...workflowData,
      modelPortfolioDetails: { ...modelPortfolioData, ...data },
    });

    if (updateWorkflowContext) {
      await updateWorkflowContext();
    }
  };

  const handleSaveForLater = async () => {
    try {
      const savedModelPortfolioData =
        await saveModelPortfolio(modelPortfolioData);
      const savedModel = {
        ...savedModelPortfolioData,
        originalValues: savedModelPortfolioData,
      };
      setModelPortfolioData(savedModel);
      setWorkflowData({
        ...workflowData,
        modelPortfolioDetails: savedModel,
      });
    } catch {
      // Error is already handled by saveModelPortfolio
    }
  };

  const handleCreateModelPortfolio = async () => {
    try {
      const savedModelPortfolioData = await saveModelPortfolio(
        modelPortfolioData,
        true,
      );
      sendAnalytic(ovAnalyticsEvents.modalSubmit('model_portfolio', 'create'), {
        modelId: savedModelPortfolioData?.id,
        timestamp: new Date().toISOString(),
      });

      // Calculate duration and send analytics before proceeding to next step
      const stepEndTime = new Date();
      const formDuration = calculateDurationSeconds(stepStartTime.current, {
        endTime: stepEndTime,
      });

      sendAnalytic(ovAnalyticsEvents.modelPortfolioWorkflowStep4Complete, {
        stepNumber: 4,
        stepName: 'ADD_IPS_AND_ASSET_CLASSES_DISTRIBUTION',
        formDuration,
        timestamp: stepEndTime.toISOString(),
        workflowCompletionId: workflowCompletion?.id,
      });

      await onNext();

      if (savedModelPortfolioData?.id) {
        navigate(`/modelPortfolios/${savedModelPortfolioData.id}`);
      }
    } catch {
      // Error is already handled by saveModelPortfolio
    }
  };

  return (
    <AddModelPortfoliosIPSAndAssetClassesVisual
      options={options}
      workflowCompletion={workflowCompletion}
      stepLoading={stepLoading}
      modelPortfolioData={modelPortfolioData}
      setModelPortfolioData={handleUpdateModelPortfolioData}
      onSaveForLater={handleSaveForLater}
      onCreateModelPortfolio={handleCreateModelPortfolio}
      loading={loading}
    />
  );
};
